import { SocialMediaFields } from "@contracts/creatorPublicProfileFields";
import { SocialChannels } from "@contracts/socialNetworks";

// The context that provided the input assets
export type SocialPostTypeId = "collabInput" | "adventure" | "location" | "community-upload" | "customer-upload";
// The source entity those assets were ultimately tied to.
// Matters for locations since they can be crator visit specific or not
export type SourceEntityType = "creatorvisit" | "myuploads" | "communityupload";
export interface SocialPostType {
  id: SocialPostTypeId | null;
  title: string;
  description: string;
}

export interface SocialPostCreatePayload {
  contextTitle: string;
  contextType: string;
  contextId: string;
  assetIdsToInclude: Array<string>;
}

export enum SocialPostStatus {
  Draft = "Draft",
  Approved = "Approved",
  Unscheduled = "Unscheduled",
  Published = "Published",
  Error = "Error",
}

export enum SocialPostPublishType {
  Post = "Post",
  Story = "Story",
  Reel = "Reel",
}

export enum SocialChannelStatus {
  Connected = "Connected",
  AuthNeeded = "AuthNeeded",
  Deleted = "Deleted",
}

export interface PostedItemDetails {
  socialChannel: SocialChannels;
  postUrl: string;
  status: PostedItemStatus | null;
  error: string | null;
}

export interface SocialPostChannelOverride {
  socialChannel: SocialChannels;
  caption: string | null;
  assetIds: Array<string>;
}

export enum PostedItemStatus {
  Posted,
  Error,
  Unknown,
}

export interface SocialMediaPostData {
  assetIds: Array<string>;
  fallbackAssetIds: Array<string> | null;
  caption: string | null;
  captionStructure?: string | null;
  useChannelOverrides?: boolean;
  channelOverrides?: Array<SocialPostChannelOverride> | null;
  postType?: SocialPostPublishType | null;
  contextId: string;
  contextTitle: string | null;
  contextType: SocialPostTypeId | null;
  sourceEntityType: SourceEntityType | null;
  sourceEntityId: string | null;
  createdBy: string;
  createdDate: string;
  customerId: string;
  calendarId?: string | null;
  deletedDate: string | null;
  status: SocialPostStatus | null;
  id: string;
  pkey: string;
  updatedBy: string;
  updatedDate: string;
  zippedDownloadId: string | null;
  postToAccounts: Array<SocialChannels>;
  postedDetails: Array<PostedItemDetails>;
  weeklyRunId?: string | null;
  createdWithAI: boolean;
  autoGenerated: boolean;
  scheduledToPostAt: ScheduledPostDateTime | null;
  postedOn?: string | null;
  _etag: string;
  weekKey: string | null;
  rating?: number | null;
  editCount?: number | null;
}

export interface SocialPostCaptionAssistRequest {
  postId: string;
  currentCaption: string;
  userInstruction: string;
  quickAction: string;
  assetIds: Array<string>;
}

export interface SocialPostCaptionAssistResponse {
  caption: string;
  requestId: string | null;
}

export interface ScheduledPostDateTime {
  // What the user intended (no implicit zone conversion)
  localDateTime: string;
  // Zone (IANA code) that the localDateTime is in
  timeZone: string;
  // The local fields converted to UTC
  postAtUtc: string;
}

export interface GptImageSelectionResult {
  index: number;
  filename: string;
  reason: string;
}

export interface GptImageSelectionReturnItem {
  imagesSelected: Array<GptImageSelectionResult>;
  caption: string;
  captionStructure?: string | null;
  context: string;
  requestId?: string;
}

export interface SocialPostGenResult {
  post: SocialMediaPostData;
  debug: GptImageSelectionReturnItem;
  imagesInputForDebug: Array<string>;
}

export interface SocialPostGenResultPayload {
  results: Array<SocialPostGenResult>;
}

// Properties nested under excludeFromIndex are NOT indexed in Cosmos DB.
export interface ImageSelectAIDebugDataUnindexed {
  debugInfo: GptImageSelectionReturnItem;
  imagesInput: Array<string>;
  inputPrompt: string;
}

export interface ImageSelectAIDebugData {
  customerId: string;
  sourceEntityType: string;
  sourceEntityId: string;
  excludeFromIndex: ImageSelectAIDebugDataUnindexed;
  // Evaluation results for the generated social media post
  evalResults?: Array<EvalResultData>;
}

export interface EvalResultData {
  evalRequestId: string;
  customerId: string;
  entityId: string; // Id of the entity this was for
  judgeType: string; // e.g., PhotoQuality, CaptionDuplication
  score: number | null; // 1-10 typically
  pass: boolean | null; // optional pass/fail when score isn't applicable
  scoreJustification: string; // rationale text
  confidence: number | null; // optional confidence
  runId?: string; // optional run id to correlate multiple results from the same run
}

export interface AdventureCreatorSummary {
  creatorId: string;
  displayName: string;
  profilePhoto: string;
  socialFields: SocialMediaFields;
}
export interface StepDetails {
  proTip: string;
  stepDetails: string;
  locationName: string;
}
export interface AdventureTextSummary {
  adventureId: string;
  uniqueName: string;
  adventureSummary: string;
  adventureTitle: string;
  steps: Array<StepDetails>;
}

/** Query filter for summary-details-by-summaryId: which media types to return. */
export enum SocialShareSummaryContentType {
  Photos = "photos",
  Videos = "videos",
  All = "all",
}

export interface SocialShareSummaryPayload {
  adventureCreatorSummaries: Array<AdventureCreatorSummary>;
  assets: Array<string>;
  title: string;
  sourceGroups?: Array<SocialShareSummarySourceGroup>;
}

export interface SocialShareSummarySourceGroup {
  summaryId: string;
  title: string;
  assetIds: Array<string>;
}

export interface UpdatePostScheduledAtPayload {
  localDateTime: string;
  timeZoneId: string;
}

export interface UpdatePostScheduledAtResponse {
  post: SocialMediaPostData;
  wasUpdated: boolean;
  newWeekKey: string;
}

export interface SocialPostWeeklyRun {
  heading: string;
  weekKey: string; // year, month, day format. Ex. 2025-04-06
  isCurrentWeek: boolean;
  weeklyAutoGenerated: Array<SocialMediaPostData>;
}

export interface SocialPostsStructured {
  manualAICreated: Array<SocialMediaPostData>;
  weeklyAutoGenerated: Array<SocialPostWeeklyRun>;
  legacyCreated: Array<SocialMediaPostData>;
}
